/**
 * ProductsFilterSheet — the mobile Products filter bottom sheet. A lean category
 * multi-select chip grid + Clear all. Selected categories feed `categoryIds`
 * into useProductsQuery. Sibling of OrdersFilterSheet (trimmed to one facet).
 */

import { ModalSheet } from "@a2z/ui";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import type { CategoryView } from "@/lib/api/types";
import type { ProductsFilterState } from "./products-mobile-model";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  categories: CategoryView[];
  value: ProductsFilterState;
  onChange: (next: ProductsFilterState) => void;
};

export function ProductsFilterSheet({ isOpen, onClose, categories, value, onChange }: Props) {
  const t = useTranslations();
  const locale = useLocale();

  const total = value.category.length;

  const toggle = (id: string) => {
    const next = value.category.includes(id)
      ? value.category.filter((x) => x !== id)
      : [...value.category, id];
    onChange({ ...value, category: next });
  };

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="sm" surface="content">
      <div className="flex max-h-[80vh] flex-col">
        <div className="flex items-center gap-2.5 px-4 pb-2 pt-4">
          <span className="text-[color:var(--text-muted)]">
            <FilterIcon />
          </span>
          <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.products.mFilterTitle)}
          </h2>
          <span className="flex-1" />
          {total > 0 ? (
            <button
              type="button"
              onClick={() => onChange({ category: [] })}
              className="text-[12.5px] font-semibold text-[color:var(--text-secondary)]"
            >
              {t(keys.orders.clearAll)}
            </button>
          ) : null}
        </div>

        <div className="overflow-y-auto px-4 pb-5 pt-1">
          <div className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
            {t(keys.products.categories)}
          </div>
          {categories.length === 0 ? (
            <span className="text-[12.5px] text-[color:var(--text-muted)]">
              {t(keys.common.empty)}
            </span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <Chip
                  key={c.id}
                  label={pickLocalized(locale, c.nameAr, c.nameEn)}
                  on={value.category.includes(c.id)}
                  onClick={() => toggle(c.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalSheet>
  );
}

function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full px-3 py-2 text-[13px] font-semibold transition-colors"
      style={{
        background: on ? "color-mix(in srgb, var(--accent-500) 14%, transparent)" : "transparent",
        border: `1px solid ${on ? "var(--accent-500)" : "var(--line)"}`,
        color: on ? "var(--accent-500)" : "var(--text-muted)",
      }}
    >
      {label}
    </button>
  );
}

function FilterIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      width={17}
      height={17}
      strokeWidth={1.9}
    >
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}
